import { useEffect, useRef, useState } from 'react';

/**
 * Measure an element's content-box size and keep it current as it resizes.
 *
 * Used by the operations-panel media region (#208) to lay pinned tiles out so
 * they fill the reserved area: the split/grid choice and the per-tile pixel
 * height depend on the live width/height of the region, not a fixed constant.
 *
 * SSR/test-safe: returns {0,0} until measured and no-ops when `ResizeObserver`
 * is unavailable (the caller falls back to a default tile size).
 */
export function useElementSize<T extends HTMLElement>(): [
  React.RefObject<T>,
  { width: number; height: number },
] {
  const ref = useRef<T>(null);
  const [size, setSize] = useState<{ width: number; height: number }>({ width: 0, height: 0 });

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    // Seed immediately so the first paint after mount already has a real size.
    setSize({ width: el.clientWidth, height: el.clientHeight });
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver((entries) => {
      const cr = entries[0]?.contentRect;
      if (cr) setSize({ width: cr.width, height: cr.height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return [ref, size];
}
